import {
  Group,
  NumberInput,
  Paper,
  Stack,
  Text,
  TextInput
} from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { cloneElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { PipelineType } from '@/types/vlm'
import { MILESTONE_STATE_CONFIG } from '../vlmPipelineConfig'
import { MilestoneServerPicker } from './MilestoneServerPicker'
import { PipelineFormValues } from './VlmPipelineForm'

// Same-source milestone pipelines and patrols explain alarms differently from
// the cross-source default.
const MILESTONE_ACTION_INFO: Partial<Record<PipelineType, string>> = {
  milestone: 'vlm.pipelines.create.actions.milestone.info.sameSource',
  virtual_patrol: 'vlm.pipelines.create.actions.milestone.info.patrol'
}

type MilestoneActionFormProps = {
  form: UseFormReturnType<PipelineFormValues>
  index: number
}

export const MilestoneActionForm = ({
  form,
  index
}: MilestoneActionFormProps) => {
  const intl = useIntl()
  const { pipelineType } = form.values
  const action = form.values.actions[index]
  const selectedServerId =
    action.type === 'milestone' ? action.milestone_server_id : ''

  return (
    <Stack gap="md" mt="md">
      <PlatformAlert variant="info" compact>
        <FormattedMessage
          id={
            (pipelineType && MILESTONE_ACTION_INFO[pipelineType]) ??
            'vlm.pipelines.create.actions.milestone.info.crossSource'
          }
        />
      </PlatformAlert>

      {pipelineType !== 'milestone' && pipelineType !== 'virtual_patrol' && (
        <PlatformAlert variant="info" compact>
          <FormattedMessage id="vlm.pipelines.create.actions.milestone.crossSourceCameraNames.info" />
        </PlatformAlert>
      )}

      <MilestoneServerPicker
        value={selectedServerId}
        label={intl.formatMessage({
          id: 'vlm.pipelines.create.actions.milestone.server.label'
        })}
        error={form.errors[`actions.${index}.milestone_server_id`]}
        deviceId={form.values.deviceId}
        onChange={(serverId) =>
          form.setFieldValue(`actions.${index}.milestone_server_id`, serverId)
        }
      />

      {MILESTONE_STATE_CONFIG.map(({ key, icon, labelId, descriptionId }) => (
        <Paper key={key} bg="gray.0" p="md" radius="md">
          <Stack gap="xs">
            <div>
              <Group gap={6} align="center">
                {cloneElement(icon, { size: 18 })}
                <Text size="sm" fw={600}>
                  <FormattedMessage id={labelId} />
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                <FormattedMessage id={descriptionId} />
              </Text>
            </div>

            <Group gap="xs" align="flex-start" wrap="nowrap">
              <TextInput
                size="sm"
                style={{ flex: 1 }}
                label={intl.formatMessage({
                  id: 'vlm.pipelines.create.actions.milestone.state.name'
                })}
                {...form.getInputProps(`actions.${index}.states.${key}.name`)}
              />

              <NumberInput
                size="sm"
                w={160}
                min={1}
                allowDecimal={false}
                label={intl.formatMessage({
                  id: 'vlm.pipelines.create.actions.milestone.state.level'
                })}
                {...form.getInputProps(`actions.${index}.states.${key}.level`)}
              />
            </Group>
          </Stack>
        </Paper>
      ))}

      <PlatformAlert variant="warning" compact>
        <FormattedMessage id="vlm.pipelines.create.actions.milestone.statesWarning" />
      </PlatformAlert>
    </Stack>
  )
}
